/* eslint-disable react/display-name */

import { FZStoreItemTableProps } from '@/components/StoreProductsTable/StoreProductsTable.types';
import rowStyles from '@/components/StoreProductsTable/TableRow.module.css';
import { StoreProduct } from '@/types/menus.types';
import { Box, Grid } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FZMenuTable from './FZMenuTable';
import styles from './FZStoreItemTable.module.css';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const CellProductDescription = ({ row }: CellContext<StoreProduct, unknown>) => {
  const { productName, productDescription } = row.original;

  return (
    <Grid container direction="column" justifyContent="center" alignItems="flex-start" width="0px">
      <Grid item className={styles.description}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{productName}</div>
      </Grid>
      <Grid item className={styles.subDescription}>
        <span className={styles.productDescription}>{productDescription}</span>
      </Grid>
    </Grid>
  );
};

function getFirstEightChars(header: string): string {
  return header.substring(0, 8);
}

const getPriceDescription =
  (header: string, setPriceDeviationsData: any, t: any) =>
  ({ row }: CellContext<StoreProduct, unknown>) => {
    const { variantId, storeGroups } = row.original;
    const { storeGroupName, chosenPriceRange } = storeGroups.find((sg) => sg.storeGroupName === header) || {};
    if (!chosenPriceRange) return t('common.inactive');

    let formattedPriceRange;
    // Special handling for "Inactive" status with price
    if (chosenPriceRange.startsWith('Inactive-')) {
      const price = chosenPriceRange.replace('Inactive-', '');
      formattedPriceRange = `${t('common.inactive')} - $${price}`;
    } else if (chosenPriceRange === 'Inactive') {
      // Display "Inactive" without $ sign
      formattedPriceRange = chosenPriceRange;
    } else {
      formattedPriceRange = `$${chosenPriceRange}`.replace(/-/g, ' - ');
    }

    return formattedPriceRange.includes('-') ? (
      <div className={styles.rangePriceCell}>
        <div className={styles.noWrapPrice}>{formattedPriceRange}</div>
        <InfoOutlined
          className={styles.priceRangeInfo}
          sx={{
            width: '22px',
            height: '22px',
            color: '#266DCC',
          }}
          onClick={() =>
            setPriceDeviationsData({
              selectedGroupName: storeGroupName,
              variantIds: [variantId],
              isPriceDeviationsDrawerOpen: true,
            })
          }
        />
      </div>
    ) : (
      <span className={styles.noWrapPrice}>{formattedPriceRange}</span>
    );
  };

const StoreItemTable = (props: FZStoreItemTableProps) => {
  const { t } = useTranslation();
  const {
    changeHeaderSize,
    menuProducts,
    dynamicHeaders,
    setPriceDeviationsData,
    selectedRows,
    onRowSelectionChange,
    isLoading,
    storesAndGroups,
    isFranchiseeEditAndFreeze,
  } = props;

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;
      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count as number;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length as number;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount as number;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length as number;
      }

      const headerWithFistEightChars = getFirstEightChars(header);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <strong>{headerWithFistEightChars}</strong>
          <span>
            {count} / {total}
          </span>
        </div>
      );
    },
    [storesAndGroups],
  );

  const columns = useMemo(() => {
    const lColumns = [
      {
        header: t('menuDetails.storeItem.table.product'),
        accessorKey: 'productDescription',
        enableSorting: true,
        cell: CellProductDescription,
        sortingFn: 'sortProduct',
        columnClassname: 'productHeaderStyle',
      } as any,
    ];
    dynamicHeaders.forEach((header, index) => {
      lColumns.push({
        header: getStoreGroupsCount(header),
        accessorKey: header,
        columnClassname:
          index < dynamicHeaders.length - 1
            ? dynamicHeaders.length < 6
              ? 'dynamicHeader150'
              : 'dynamicHeader'
            : 'dynamicHeaderLastItem',
        enableSorting: true,
        cell: getPriceDescription(header, setPriceDeviationsData, t),
        sortingFn: 'sortProductByPrice',
      } as any);
    });

    return lColumns;
  }, [t, dynamicHeaders, getStoreGroupsCount, setPriceDeviationsData]);

  return (
    <div className={rowStyles.tablePage}>
      <div>
        <Box>
          <FZMenuTable
            returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
            data={menuProducts}
            isLoading={isLoading}
            enableRowSelection
            columns={columns}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            changeHeaderSize={changeHeaderSize}
            isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
          />
        </Box>
      </div>
    </div>
  );
};

export default StoreItemTable;
